import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';

import { EXO_SKELETON_SCOPE_NAME } from '../consts';

export type TUseSessionStorage<T> = {
  getSessionStorageItem: () => T | undefined;
  setSessionStorageItem: (value: T) => void;
  removeSessionStorageItem: () => void;
};

/**
 * @description A hook that provides functions to interact with a session storage item.
 * @param key - The key of the session storage item.
 * @param defaultValue - The default value will be returned if the session storage item does not exist.
 * @returns An array of functions to get, set and remove the session storage item.
 */
export function useSessionStorage<T>(
  key: string,
  defaultValue?: T,
): TUseSessionStorage<T> {
  /**
   * @description Reports an error to the Exo Skeleton API.
   * @param error - The error to report.
   * @returns {void}
   */
  const reportError = (error: unknown) => {
    report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: REPORT_TYPES.error,
      value: ` ${key} parse error in session storage`,
      context: {
        error,
      },
    });
  };

  /**
   * @description Sets the session storage item with the provided value.
   * @param value - The value to set the session storage item to.
   * @returns {void}
   */
  const setSessionStorageItem = (value: T): void => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      reportError(error);
    }
  };
  /**
   * @description Gets the session storage item.
   * @returns {T | undefined} The session storage item or undefined if it does not exist.
   */
  const getSessionStorageItem = (): T | undefined => {
    try {
      const item = window.sessionStorage.getItem(key);

      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item);
    } catch (error) {
      reportError(error);
      return undefined;
    }
  };
  /**
   * @description Removes the session storage item.
   * @returns {void}
   */
  const removeSessionStorageItem = (): void => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      reportError(error);
    }
  };
  return {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
  };
}
